import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useIsVisible } from '@hooks/useIsVisible';
import { useScrollDirection } from '@hooks/useScrollDirection';
import { useWindowSize } from '@hooks/useWindowSize';
import { useAudioController } from '@store/audio/AudioController';

const COLUMN_OFFSET = 12;
const PAGE_BANNERS_OFFSET = 80;

export function TwoColumnPage({
  leftContent,
  rightContent,
}: {
  leftContent: React.ReactNode;
  rightContent: React.ReactNode;
}) {
  const { scrollDirection, scrollPositionWhenDirectionChanged } = useScrollDirection();
  const { hideAudioPlayer } = useAudioController();
  const [marginTop, setMarginTop] = useState(0);
  const [top, setTop] = useState<number | undefined>(undefined);
  const [bottom, setBottom] = useState<number | undefined>(undefined);

  const contentRef = React.useRef<HTMLDivElement>(null);
  const topRef = React.useRef<HTMLDivElement>(null);
  const bottomRef = React.useRef<HTMLDivElement>(null);

  const isTopVisible = useIsVisible({ ref: topRef });
  const isBottomVisible = useIsVisible({ ref: bottomRef });

  const { height } = useWindowSize();

  const contentHeight = contentRef?.current?.getBoundingClientRect().height ?? 0;
  const windowHeight = height ?? 0;

  const addBottomPadding =
    !hideAudioPlayer && contentHeight > windowHeight - PAGE_BANNERS_OFFSET * 2;

  const onScrollDown = useCallback(() => {
    setTop(Math.min(COLUMN_OFFSET, windowHeight - contentHeight - COLUMN_OFFSET));
    setBottom(undefined);
    if (isTopVisible) {
      setMarginTop(Math.max(0, scrollPositionWhenDirectionChanged - PAGE_BANNERS_OFFSET));
    }
  }, [contentHeight, isTopVisible, scrollPositionWhenDirectionChanged, windowHeight]);

  const onScrollUp = useCallback(() => {
    setTop(undefined);
    setBottom(windowHeight - contentHeight - COLUMN_OFFSET);
    if (isBottomVisible) {
      const margin = Math.max(
        0,
        scrollPositionWhenDirectionChanged -
          contentHeight +
          (window.innerHeight - (PAGE_BANNERS_OFFSET + COLUMN_OFFSET * 2)),
      );
      setMarginTop(margin);
    }
  }, [contentHeight, isBottomVisible, scrollPositionWhenDirectionChanged, windowHeight]);

  useEffect(() => {
    if (scrollDirection === 'down') {
      onScrollDown();
    } else {
      onScrollUp();
    }
  }, [onScrollDown, onScrollUp, scrollDirection]);

  return (
    <div className="relative flex min-h-page justify-center overflow-x-hidden md:min-h-md-page lt:overflow-x-clip">
      <div className="flex w-full gap-20 px-4 pb-6 md:px-6 dt:max-w-content">
        <div className="min-w-0 flex-1">{leftContent}</div>
        {rightContent && (
          <div
            className="relative hidden h-full flex-[0_0_304px] lt:inline"
            style={{
              minHeight: contentHeight,
            }}
          >
            <div
              className="relative flex"
              style={{
                marginTop,
              }}
            />
            <div className="sticky mt-3 hidden h-fit lt:flex" style={{ top, bottom }}>
              <div ref={contentRef} className={clsx('flex flex-col', addBottomPadding && 'pb-28')}>
                <div className="relative" ref={topRef} />
                {rightContent}
                <div className="relative" ref={bottomRef} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
